/* queryBuilderLeafValidator.js */

/**
 * @module queryBuilderLeafValidator
 *
 * Post-Ajv validator for the JSON edit pane. Walks `thenExpression` /
 * `elseExpression` query-builder trees in each rule and flags leaves that
 * carry an unexpected property (a renamed/typo'd key), whose `id` doesn't
 * match a known column, or whose `operator` is outside the column's
 * allowed-operator list (when the column declares one).
 *
 * Pairs with the `id` enum injected by buildDynamicSchemas — Ajv catches
 * unknown ids on its own, but this validator adds:
 *   - column-typed operator checks (Ajv enums are global, not per-id)
 *   - sharper messages that name the offending column
 *
 * Custom-field paths (`customFieldValues.<key>`) are intentionally skipped
 * here — customFieldEnumValidator already covers them.
 *
 * Returns a function `(parsedValue, text) → errors[]` matching the shape
 * produced by json_schema_validator so the gutter-marker pipeline doesn't
 * need any special handling.
 */
const _ = require("lodash");
const {SchemaValidator} = require("@gluenetworks/json-editor");
const {walkQueryBuilderLeaves} = require("./expressionWalkers");

const CUSTOM_FIELD_PATH_PATTERN = /^customFieldValues\./;

// The only properties a queryBuilder leaf carries (jQuery-QueryBuilder getRules
// output). Anything else is a typo (e.g. `field` renamed to `fold`) that would
// otherwise be saved as junk.
const ALLOWED_LEAF_KEYS = new Set(["id", "field", "type", "input", "operator", "value"]);

const _pushError = (ctx, pointer, message) => {
    const {line, column} = SchemaValidator.pointerToLineColumn(ctx.text, pointer);
    ctx.errors.push({line, column, message, jsonPointer: pointer});
};

const _checkLeaf = (leaf, pointer, ctx) => {
    if (!_.isObject(leaf) || _.isArray(leaf)) {
        return;
    }
    // Flag unexpected leaf properties (a renamed/typo'd key like `fold`) — runs
    // for every leaf, including customFieldValues.* paths.
    _.each(leaf, (value, key) => {
        if (!ALLOWED_LEAF_KEYS.has(key)) {
            _pushError(ctx, `${pointer}/${key}`, `[queryBuilder] Unexpected property "${key}"`);
        }
    });
    const id = leaf.id;
    if (!id || CUSTOM_FIELD_PATH_PATTERN.test(id)) {
        return;
    }
    const column = ctx.columnsByName[id];
    if (!column) {
        _pushError(ctx, `${pointer}/id`, `[queryBuilder] Unknown field "${id}"`);
        return;
    }
    const op = leaf.operator;
    if (
        op &&
        _.isArray(column.operators) &&
        column.operators.length &&
        !column.operators.includes(op)
    ) {
        _pushError(
            ctx,
            `${pointer}/operator`,
            `[queryBuilder] Operator "${op}" is not valid for "${id}" (allowed: ${column.operators.join(
                ","
            )})`
        );
    }
};

const _walkExpressionsOnRule = (rule, basePointer, ctx) => {
    if (!_.isObject(rule)) {
        return;
    }
    if (rule.thenExpression) {
        walkQueryBuilderLeaves(rule.thenExpression, `${basePointer}/thenExpression`, (leaf, ptr) =>
            _checkLeaf(leaf, ptr, ctx)
        );
    }
    if (rule.elseExpression) {
        walkQueryBuilderLeaves(rule.elseExpression, `${basePointer}/elseExpression`, (leaf, ptr) =>
            _checkLeaf(leaf, ptr, ctx)
        );
    }
};

/**
 * Build a validator bound to the given column set.
 *
 * `qbColumns`: `[{name, label?, type?, operators?}]` — see queryBuilderColumns.
 *
 * Accepts both the policy shape (`{rules:[...]}`) and a single-rule shape
 * (the rule-edit subview's JSON pane).
 */
const buildQueryBuilderLeafValidator = (qbColumns = []) => {
    const columnsByName = _.keyBy(qbColumns, "name");
    return (parsedValue, text) => {
        if (!_.isObject(parsedValue) || _.isEmpty(columnsByName)) {
            return [];
        }
        const ctx = {errors: [], text: text || "", columnsByName};
        if (_.isArray(parsedValue.rules) && !parsedValue.condition) {
            // Policy shape (top-level `rules` array of rule objects).
            parsedValue.rules.forEach((rule, idx) =>
                _walkExpressionsOnRule(rule, `/rules/${idx}`, ctx)
            );
        } else {
            // Single-rule shape.
            _walkExpressionsOnRule(parsedValue, "", ctx);
        }
        return ctx.errors;
    };
};

module.exports = {buildQueryBuilderLeafValidator};
